/**
 * AdminPage — Clutch consultant setup portal
 *
 * Route: /admin
 *
 * Protected by a session-scoped admin key (X-Admin-Key header).
 * Key is entered once per session and stored in sessionStorage.
 *
 * Tabs:
 *   Clients     — create + list clients
 *   Engagements — create + list engagements (pick client)
 *   Runs        — create + list runs (pick engagement + instrument)
 */

import { useState, useEffect, useCallback } from 'react'
import styles from './AdminPage.module.css'
import { useAuth, isAdmin } from '../context/AuthContext'

const API_BASE = import.meta.env.VITE_API_URL
  || 'https://clutch-ocm-scorer-uqrk7imnlq-uc.a.run.app'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ClientRecord {
  client_id: number
  client_name: string
  jurisdiction_type: string
  sector: string | null
  active_flag: boolean
}

interface EngagementRecord {
  engagement_id: number
  client_id: number
  client_name: string
  engagement_name: string
  xocm_tier: string
  change_type: string | null
  start_date: string | null
  notes: string | null
  active_flag: boolean
}

interface RunRecord {
  run_id: number
  engagement_id: number
  engagement_name: string
  client_name: string
  instrument_code: string
  run_label: string
  run_date: string
  notes: string | null
  respondent_count: number
  active_flag: boolean
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function adminFetch(
  path: string,
  idToken: string | null,
  opts: RequestInit = {},
): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(opts.headers as Record<string, string> ?? {}),
    },
  })
}

// FastAPI returns detail as a string for HTTP errors, or an array of objects
// for Pydantic validation errors (e.g. [{loc, msg, type}]). Always stringify.
function formatApiError(data: unknown, status: number): string {
  if (data && typeof data === 'object') {
    const d = (data as Record<string, unknown>).detail
    if (typeof d === 'string') return d
    if (Array.isArray(d)) {
      const msgs = d
        .map((e: unknown) => (e as Record<string, unknown>).msg)
        .filter(Boolean)
        .join('; ')
      return msgs || `Error ${status}`
    }
  }
  return `Error ${status}`
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.formRow}>
      <label className={styles.formLabel}>{label}</label>
      {children}
    </div>
  )
}

function StatusMsg({ ok, msg }: { ok: boolean; msg: string }) {
  return msg ? (
    <div className={`${styles.statusMsg} ${ok ? styles.statusOk : styles.statusErr}`}>
      {msg}
    </div>
  ) : null
}

// ---------------------------------------------------------------------------
// Clients tab
// ---------------------------------------------------------------------------

function ClientsTab({ idToken }: { idToken: string | null }) {
  const [clients,  setClients]  = useState<ClientRecord[]>([])
  const [loading,  setLoading]  = useState(true)
  const [status,   setStatus]   = useState({ ok: true, msg: '' })
  const [saving,   setSaving]   = useState(false)

  const [form, setForm] = useState({
    client_name: '',
    jurisdiction_type: 'State Agency',
    sector: '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminFetch('/admin/clients', idToken)
      if (res.ok) setClients(await res.json())
      else setStatus({ ok: false, msg: `Failed to load clients (${res.status})` })
    } catch {
      setStatus({ ok: false, msg: 'Could not reach the API. Check your connection.' })
    } finally {
      setLoading(false)
    }
  }, [idToken])

  useEffect(() => { load() }, [load])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const trimmedName = form.client_name.trim()
    if (!trimmedName) {
      setStatus({ ok: false, msg: 'Organization name cannot be blank.' })
      return
    }
    setSaving(true)
    setStatus({ ok: true, msg: '' })
    try {
      const res = await adminFetch('/admin/clients', idToken, {
        method: 'POST',
        body: JSON.stringify({
          client_name: trimmedName,
          jurisdiction_type: form.jurisdiction_type,
          sector: form.sector.trim() || null,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(formatApiError(data, res.status))
      setStatus({ ok: true, msg: `Client "${data.client_name}" created (ID: ${data.client_id})` })
      setForm({ client_name: '', jurisdiction_type: 'State Agency', sector: '' })
      load()
    } catch (e: unknown) {
      setStatus({ ok: false, msg: e instanceof Error ? e.message : 'Failed to create client.' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.tabBody}>
      <section className={styles.formSection}>
        <h2 className={styles.sectionTitle}>Create Client</h2>
        <form onSubmit={handleCreate} className={styles.form}>
          <FormRow label="Organization name *">
            <input
              className={styles.input}
              value={form.client_name}
              onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
              placeholder="e.g. Lakewood Health System"
              required
            />
          </FormRow>
          <FormRow label="Jurisdiction type *">
            <select
              className={styles.select}
              value={form.jurisdiction_type}
              onChange={e => setForm(f => ({ ...f, jurisdiction_type: e.target.value }))}
            >
              {['State Agency','County','City','Nonprofit'].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </FormRow>
          <FormRow label="Sector (optional)">
            <input
              className={styles.input}
              value={form.sector}
              onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}
              placeholder="e.g. Healthcare, Public Safety"
            />
          </FormRow>
          <StatusMsg {...status} />
          <button className={styles.submitBtn} type="submit" disabled={saving}>
            {saving ? 'Creating…' : '+ Create Client'}
          </button>
        </form>
      </section>

      <section className={styles.listSection}>
        <h2 className={styles.sectionTitle}>Existing Clients</h2>
        {loading ? (
          <p className={styles.loadingNote}>Loading…</p>
        ) : clients.length === 0 ? (
          <p className={styles.emptyNote}>No clients yet.</p>
        ) : (
          <div className={styles.tableScroll}><table className={styles.listTable}>
            <thead>
              <tr>
                <th>ID</th><th>Name</th><th>Jurisdiction</th><th>Sector</th><th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.client_id} className={!c.active_flag ? styles.archivedRow : undefined}>
                  <td className={styles.tdId}>{c.client_id}</td>
                  <td className={styles.tdName}>
                    {c.client_name}
                    {!c.active_flag && <span className={styles.archivedBadge}>Archived</span>}
                  </td>
                  <td>{c.jurisdiction_type}</td>
                  <td>{c.sector ?? '—'}</td>
                  <td>
                    <button
                      className={c.active_flag ? styles.archiveBtn : styles.restoreBtn}
                      onClick={() => handleToggleClientActive(c.client_id, c.active_flag, c.client_name)}
                    >
                      {c.active_flag ? 'Archive' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </section>
    </div>
  )

  async function handleToggleClientActive(id: number, currentActive: boolean, name: string) {
    const action = currentActive ? 'archive' : 'restore'
    if (!window.confirm(`${currentActive ? 'Archive' : 'Restore'} "${name}"?`)) return
    try {
      const res = await adminFetch(`/admin/clients/${id}/active`, idToken, {
        method: 'PATCH',
        body: JSON.stringify({ active_flag: !currentActive }),
      })
      if (res.ok) {
        setStatus({ ok: true, msg: `Client "${name}" ${action}d.` })
        load()
      } else {
        const data = await res.json()
        setStatus({ ok: false, msg: formatApiError(data, res.status) })
      }
    } catch {
      setStatus({ ok: false, msg: `Failed to ${action} client.` })
    }
  }
}

// ---------------------------------------------------------------------------
// Engagements tab
// ---------------------------------------------------------------------------

function EngagementsTab({ idToken }: { idToken: string | null }) {
  const [clients,     setClients]     = useState<ClientRecord[]>([])
  const [engagements, setEngagements] = useState<EngagementRecord[]>([])
  const [loading,     setLoading]     = useState(true)
  const [status,      setStatus]      = useState({ ok: true, msg: '' })
  const [saving,      setSaving]      = useState(false)

  const [form, setForm] = useState({
    client_id: '',
    engagement_name: '',
    xocm_tier: 'Foundational',
    change_type: '',
    start_date: '',
    notes: '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [cr, er] = await Promise.all([
        adminFetch('/admin/clients', idToken),
        adminFetch('/admin/engagements', idToken),
      ])
      if (cr.ok) setClients(await cr.json())
      if (er.ok) setEngagements(await er.json())
      if (!cr.ok || !er.ok) setStatus({ ok: false, msg: `API error loading data (${!cr.ok ? cr.status : er.status})` })
    } catch {
      setStatus({ ok: false, msg: 'Could not reach the API. Check your connection.' })
    } finally {
      setLoading(false)
    }
  }, [idToken])

  useEffect(() => { load() }, [load])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setStatus({ ok: true, msg: '' })
    try {
      const res = await adminFetch('/admin/engagements', idToken, {
        method: 'POST',
        body: JSON.stringify({
          client_id: Number(form.client_id),
          engagement_name: form.engagement_name.trim(),
          xocm_tier: form.xocm_tier,
          change_type: form.change_type.trim() || null,
          start_date: form.start_date || null,
          notes: form.notes.trim() || null,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(formatApiError(data, res.status))
      setStatus({ ok: true, msg: `Engagement "${data.engagement_name}" created (ID: ${data.engagement_id})` })
      setForm({ client_id: '', engagement_name: '', xocm_tier: 'Foundational', change_type: '', start_date: '', notes: '' })
      load()
    } catch (e: unknown) {
      setStatus({ ok: false, msg: e instanceof Error ? e.message : 'Failed.' })
    }
    setSaving(false)
  }

  return (
    <div className={styles.tabBody}>
      <section className={styles.formSection}>
        <h2 className={styles.sectionTitle}>Create Engagement</h2>
        <form onSubmit={handleCreate} className={styles.form}>
          <FormRow label="Client *">
            <select
              className={styles.select}
              value={form.client_id}
              onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
              required
            >
              <option value="">— select client —</option>
              {clients.filter(c => c.active_flag).map(c => (
                <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
              ))}
            </select>
          </FormRow>
          <FormRow label="Engagement name *">
            <input
              className={styles.input}
              value={form.engagement_name}
              onChange={e => setForm(f => ({ ...f, engagement_name: e.target.value }))}
              placeholder="e.g. EHR Implementation — Wave 1"
              required
            />
          </FormRow>
          <FormRow label="xOCM tier *">
            <select
              className={styles.select}
              value={form.xocm_tier}
              onChange={e => setForm(f => ({ ...f, xocm_tier: e.target.value }))}
            >
              {['Foundational','Targeted','Comprehensive','High-Risk Enterprise'].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </FormRow>
          <FormRow label="Change type">
            <input
              className={styles.input}
              value={form.change_type}
              onChange={e => setForm(f => ({ ...f, change_type: e.target.value }))}
              placeholder="e.g. Technology, Process, Organizational"
            />
          </FormRow>
          <FormRow label="Start date">
            <input
              className={styles.input}
              type="date"
              value={form.start_date}
              onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
            />
          </FormRow>
          <FormRow label="Context / habit description">
            <textarea
              className={styles.textarea}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Describe the change context (CODE-C) or target habit (HFA) shown to respondents during the assessment."
              rows={3}
            />
          </FormRow>
          <StatusMsg {...status} />
          <button className={styles.submitBtn} type="submit" disabled={saving || !form.client_id}>
            {saving ? 'Creating…' : '+ Create Engagement'}
          </button>
        </form>
      </section>

      <section className={styles.listSection}>
        <h2 className={styles.sectionTitle}>Existing Engagements</h2>
        {loading ? (
          <p className={styles.loadingNote}>Loading…</p>
        ) : engagements.length === 0 ? (
          <p className={styles.emptyNote}>No engagements yet.</p>
        ) : (
          <div className={styles.tableScroll}><table className={styles.listTable}>
            <thead>
              <tr><th>ID</th><th>Client</th><th>Engagement</th><th>Tier</th><th>Start</th><th></th></tr>
            </thead>
            <tbody>
              {engagements.map(eng => (
                <tr key={eng.engagement_id} className={!eng.active_flag ? styles.archivedRow : undefined}>
                  <td className={styles.tdId}>{eng.engagement_id}</td>
                  <td>{eng.client_name}</td>
                  <td className={styles.tdName}>
                    {eng.engagement_name}
                    {!eng.active_flag && <span className={styles.archivedBadge}>Archived</span>}
                  </td>
                  <td>{eng.xocm_tier}</td>
                  <td>{eng.start_date ?? '—'}</td>
                  <td>
                    <button
                      className={eng.active_flag ? styles.archiveBtn : styles.restoreBtn}
                      onClick={() => handleToggleEngagementActive(eng.engagement_id, eng.active_flag, eng.engagement_name)}
                    >
                      {eng.active_flag ? 'Archive' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </section>
    </div>
  )

  async function handleToggleEngagementActive(id: number, currentActive: boolean, name: string) {
    const action = currentActive ? 'archive' : 'restore'
    if (!window.confirm(`${currentActive ? 'Archive' : 'Restore'} "${name}"?`)) return
    try {
      const res = await adminFetch(`/admin/engagements/${id}/active`, idToken, {
        method: 'PATCH',
        body: JSON.stringify({ active_flag: !currentActive }),
      })
      if (res.ok) {
        setStatus({ ok: true, msg: `Engagement "${name}" ${action}d.` })
        load()
      } else {
        const data = await res.json()
        setStatus({ ok: false, msg: formatApiError(data, res.status) })
      }
    } catch {
      setStatus({ ok: false, msg: `Failed to ${action} engagement.` })
    }
  }
}

// ---------------------------------------------------------------------------
// Runs tab
// ---------------------------------------------------------------------------

function RunsTab({ idToken }: { idToken: string | null }) {
  const [engagements, setEngagements] = useState<EngagementRecord[]>([])
  const [runs,        setRuns]        = useState<RunRecord[]>([])
  const [loading,     setLoading]     = useState(true)
  const [status,      setStatus]      = useState({ ok: true, msg: '' })
  const [saving,      setSaving]      = useState(false)

  const [form, setForm] = useState({
    engagement_id: '',
    instrument_code: 'CODEC-v3',
    run_label: 'Baseline',
    run_date: new Date().toISOString().slice(0, 10),
    notes: '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [er, rr] = await Promise.all([
        adminFetch('/admin/engagements', idToken),
        adminFetch('/admin/runs', idToken),
      ])
      if (er.ok) setEngagements(await er.json())
      if (rr.ok) setRuns(await rr.json())
      if (!er.ok || !rr.ok) setStatus({ ok: false, msg: `API error loading data (${!er.ok ? er.status : rr.status})` })
    } catch {
      setStatus({ ok: false, msg: 'Could not reach the API. Check your connection.' })
    } finally {
      setLoading(false)
    }
  }, [idToken])

  useEffect(() => { load() }, [load])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setStatus({ ok: true, msg: '' })
    try {
      const res = await adminFetch('/admin/runs', idToken, {
        method: 'POST',
        body: JSON.stringify({
          engagement_id: Number(form.engagement_id),
          instrument_code: form.instrument_code,
          run_label: form.run_label,
          run_date: form.run_date,
          notes: form.notes.trim() || null,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(formatApiError(data, res.status))
      setStatus({ ok: true, msg: `Run created! Use the Outreach tab to upload contacts and send individual links.` })
      setForm(f => ({ ...f, notes: '' }))
      load()
    } catch (e: unknown) {
      setStatus({ ok: false, msg: e instanceof Error ? e.message : 'Failed.' })
    }
    setSaving(false)
  }

  const LABEL_ORDER = ['Baseline', 'Pre-Go-Live', '30-Day', '90-Day', 'Custom']

  return (
    <div className={styles.tabBody}>
      <section className={styles.formSection}>
        <h2 className={styles.sectionTitle}>Create Assessment Run</h2>
        <form onSubmit={handleCreate} className={styles.form}>
          <FormRow label="Engagement *">
            <select
              className={styles.select}
              value={form.engagement_id}
              onChange={e => setForm(f => ({ ...f, engagement_id: e.target.value }))}
              required
            >
              <option value="">— select engagement —</option>
              {engagements.filter(eng => eng.active_flag).map(eng => (
                <option key={eng.engagement_id} value={eng.engagement_id}>
                  {eng.client_name} — {eng.engagement_name}
                </option>
              ))}
            </select>
          </FormRow>
          <FormRow label="Instrument *">
            <select
              className={styles.select}
              value={form.instrument_code}
              onChange={e => setForm(f => ({ ...f, instrument_code: e.target.value }))}
            >
              <option value="CODEC-v3">CODE-C Readiness–Resistance</option>
              <option value="HFA-v3">HFA Habit Adoption</option>
            </select>
          </FormRow>
          <FormRow label="Run label *">
            <select
              className={styles.select}
              value={form.run_label}
              onChange={e => setForm(f => ({ ...f, run_label: e.target.value }))}
            >
              {LABEL_ORDER.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </FormRow>
          <FormRow label="Run date *">
            <input
              className={styles.input}
              type="date"
              value={form.run_date}
              onChange={e => setForm(f => ({ ...f, run_date: e.target.value }))}
              required
            />
          </FormRow>
          <FormRow label="Context note (optional)">
            <textarea
              className={styles.textarea}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Override the engagement context for this specific run (leave blank to inherit from engagement)."
              rows={2}
            />
          </FormRow>
          <StatusMsg {...status} />
          <button className={styles.submitBtn} type="submit" disabled={saving || !form.engagement_id}>
            {saving ? 'Creating…' : '+ Create Run'}
          </button>
        </form>
      </section>

      <section className={styles.listSection}>
        <h2 className={styles.sectionTitle}>Existing Runs</h2>
        {loading ? (
          <p className={styles.loadingNote}>Loading…</p>
        ) : runs.length === 0 ? (
          <p className={styles.emptyNote}>No runs yet.</p>
        ) : (
          <div className={styles.tableScroll}><table className={styles.listTable}>
            <thead>
              <tr>
                <th>ID</th><th>Client</th><th>Engagement</th>
                <th>Instrument</th><th>Label</th><th>Date</th>
                <th>n</th><th></th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.run_id} className={!r.active_flag ? styles.archivedRow : undefined}>
                  <td className={styles.tdId}>{r.run_id}</td>
                  <td>{r.client_name}</td>
                  <td className={styles.tdName}>
                    {r.engagement_name}
                    {!r.active_flag && <span className={styles.archivedBadge}>Archived</span>}
                  </td>
                  <td>
                    <span className={r.instrument_code === 'CODEC-v3' ? styles.tagCodec : styles.tagHfa}>
                      {r.instrument_code === 'CODEC-v3' ? 'CODE-C' : 'HFA'}
                    </span>
                  </td>
                  <td>{r.run_label}</td>
                  <td>{r.run_date}</td>
                  <td className={styles.tdCenter}>{r.respondent_count}</td>
                  <td>
                    <button
                      className={r.active_flag ? styles.archiveBtn : styles.restoreBtn}
                      onClick={() => handleToggleRunActive(r.run_id, r.active_flag, r.run_label)}
                    >
                      {r.active_flag ? 'Archive' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </section>
    </div>
  )

  async function handleToggleRunActive(id: number, currentActive: boolean, label: string) {
    const action = currentActive ? 'archive' : 'restore'
    if (!window.confirm(`${currentActive ? 'Archive' : 'Restore'} run "${label}"? This will ${currentActive ? 'hide it from' : 'show it on'} the dashboard.`)) return
    try {
      const res = await adminFetch(`/admin/runs/${id}/active`, idToken, {
        method: 'PATCH',
        body: JSON.stringify({ active_flag: !currentActive }),
      })
      if (res.ok) {
        setStatus({ ok: true, msg: `Run "${label}" ${action}d.` })
        load()
      } else {
        const data = await res.json()
        setStatus({ ok: false, msg: formatApiError(data, res.status) })
      }
    } catch {
      setStatus({ ok: false, msg: `Failed to ${action} run.` })
    }
  }
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type Tab = 'clients' | 'engagements' | 'runs'

export function AdminPage() {
  const { idToken, platformUser, loading } = useAuth()
  const [tab, setTab] = useState<Tab>('clients')

  // Auth still resolving
  if (loading) return null

  // Restrict to clutch_admin only
  if (!isAdmin(platformUser)) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <div className={styles.eyebrow}>Clutch Admin</div>
            <h1 className={styles.pageTitle}>Access Denied</h1>
            <p className={styles.pageSub}>This page is restricted to Clutch administrators.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <div className={styles.eyebrow}>Clutch Admin</div>
          <h1 className={styles.pageTitle}>Setup Portal</h1>
          <p className={styles.pageSub}>Create clients, engagements, and assessment runs.</p>
        </div>
      </div>

      <div className={styles.tabs}>
        {(['clients', 'engagements', 'runs'] as Tab[]).map(t => (
          <button
            key={t}
            className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'clients'     && <ClientsTab     idToken={idToken} />}
      {tab === 'engagements' && <EngagementsTab idToken={idToken} />}
      {tab === 'runs'        && <RunsTab        idToken={idToken} />}
    </div>
  )
}
